import { ComplianceProgramTimelineDays } from "@apps/programs/components/compliance-program-timeline";
import { sendingKinds } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { conditionalTimelineMessage } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import Loading from "@components/loading/loading";

import { ProgramForm } from "../types";
import useProgramFormTimeline, {
  timelineGapMessage,
} from "../use-program-form-timeline";

const messages = (count: number) =>
  `${count} ${count === 1 ? "message" : "messages"}`;

/**
 * What the review this form describes will do, day by day, before it is saved.
 *
 * The same panel as the participants preview on the Participants tab, applied to
 * the other half of the question: that one answers "who takes part", this one
 * answers "which messages go out, when, and who hears nothing". It renders the
 * review page's own Timeline component, so a draft and the page that will show it
 * once it is saved cannot look different.
 *
 * Always on, with no control to open it. Planning a review resolves the whole
 * roster, which is why this began behind a button; the gaps in
 * `useProgramFormTimeline` pay for it instead. A form that has not said when the
 * review runs, what it runs, or who is in it is answered from the form itself and
 * never reaches the backend, so the query only goes out once the admin has said
 * enough to want the answer.
 */
const ProgramFormTimelinePreview = ({
  proposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const { noun } = useProgramsSection();
  const { rows, loading, error, refusal, gap, conditionalKickoffAfterDays } =
    useProgramFormTimeline(proposedComplianceProgram);

  // Only counted when there is a plan to count. A form that has just lost its last
  // step still has an answer in hand, about the form it used to be.
  const plan = gap === null ? rows : undefined;
  const sends = (plan ?? []).filter((row) => sendingKinds.includes(row.kind));

  return (
    // Heading, then a sentence, then a bordered box — the same three parts in the
    // same order as the section beside it, so the two boxes start on one line
    // instead of one of them floating a heading's height above the other.
    <div>
      <div className="flex items-baseline gap-2">
        <span className="tf-header-base-dark">Timeline</span>
        {plan && (
          <span className="truncate text-sm text-gray-500">
            {messages(sends.length)}
          </span>
        )}
        {/* In the heading, so that starting a refresh does not move the plan
            underneath the admin's eye. */}
        {gap === null && loading && plan && <Loading size="4" mini />}
      </div>

      {/* Two lines in this column, as the sentence under the heading beside it is
          two lines in that one. Both boxes then start at the same height. */}
      <div className="mt-1 text-gray-500">
        When each step opens and falls due, and what that sends to whom.
      </div>

      {/* What the dates below are, when they are not the ones on the form. Said
          here rather than left for the admin to notice, because a conditional
          review dated from today reads as a plan of the review on the screen. */}
      {conditionalKickoffAfterDays !== null && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(
            noun.toLowerCase(),
            conditionalKickoffAfterDays,
          ).map((sentence) => (
            <p key={sentence} className="mt-1 first:mt-0">
              {sentence}
            </p>
          ))}
        </div>
      )}

      {/* A failed request must never read as "this sends nothing", and must never
          leave an old plan looking freshly checked. Shown whether or not there is
          something to show under it. The backend's own words come through when it
          has any: a rule reaching outside the admin's tree is refused with a
          sentence saying what to change, which "reload the page" would bury. */}
      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
          {refusal ||
            (plan
              ? "This timeline could not be refreshed, so it may be out of date. Reload the page to try again."
              : "This timeline could not be loaded. Reload the page to try again.")}
        </div>
      )}

      {/* Scrolls inside itself rather than growing: beside the form it is sticky,
          and a plan taller than the window would put its last day out of reach of
          a column that never scrolls. */}
      <div className="mt-3 max-h-[calc(100vh-12rem)] overflow-y-auto">
        <ComplianceProgramTimelineDays
          rows={plan ?? []}
          empty={
            gap !== null ? (
              timelineGapMessage(gap, noun.toLowerCase(), { here: true })
            ) : loading ? (
              <Loading size="5" />
            ) : // The banner above already says what went wrong; saying
            // "reload the page" twice in two boxes reads as two problems.
            error ? null : (
              `This ${noun.toLowerCase()} has nothing scheduled yet.`
            )
          }
        />
      </div>
    </div>
  );
};

export default ProgramFormTimelinePreview;
